import { GTP_CHART_MESH_CLASSES, GTP_PLOT_EXCLUDED_CLASSES } from '@/utils/gtpOptions';

export type GeneGtpItem = {
  mesh_class_id?: string;
  mesh_class_name?: string;
  tces?: number;
};

export type GtpChartRow = {
  id: string;
  label: string;
  fullName: string;
  tces: number;
  color: string;
};

export const GTP_POSITIVE_COLOR = '#b2182b';
export const GTP_NEGATIVE_COLOR = '#2166ac';
export const GTP_NEUTRAL_COLOR = '#f7f7f7';

function tcesNumber(value: unknown): number {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function truncateLabel(name: string, max = 32): string {
  if (name.length <= max) return name;
  return `${name.slice(0, max - 1)}…`;
}

export function formatTcesScore(value: unknown): string {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return '';
  return String(Number(numeric.toFixed(3)));
}

export function parseGtpItems(value: unknown): GeneGtpItem[] {
  if (!Array.isArray(value)) return [];
  return value.filter(item => item && typeof item === 'object') as GeneGtpItem[];
}

function formatGtpItem(item: unknown): string {
  if (!item || typeof item !== 'object') return '';
  const { mesh_class_name, tces } = item as GeneGtpItem;
  const name = typeof mesh_class_name === 'string' ? mesh_class_name.trim() : '';
  if (!name) return '';
  const score = formatTcesScore(tces);
  return score ? `${name} (${score})` : name;
}

export function getGtpItemsFromRow(data: Record<string, unknown> | null | undefined): GeneGtpItem[] {
  if (!data) return [];
  return parseGtpItems(data.GTAP ?? data.gtap ?? data.GTP ?? data.gtp);
}

export function getGtpClassCountLabel(items: GeneGtpItem[]): string {
  if (items.length === 1) return '1 class';
  return `${items.length} classes`;
}

export function getMaxTcesItem(items: GeneGtpItem[]): GeneGtpItem | undefined {
  let best: GeneGtpItem | undefined;
  let bestScore = -Infinity;

  items.forEach(item => {
    const score = tcesNumber(item.tces);
    if (!best || score > bestScore) {
      best = item;
      bestScore = score;
    }
  });

  return best;
}

export function formatMaxTcesGtp(items: GeneGtpItem[]): string {
  const top = getMaxTcesItem(items);
  if (!top) return '-';
  return formatGtpItem(top) || '-';
}

export function getGtpSummary(items: GeneGtpItem[]): string {
  if (items.length === 0) return '-';

  const top = [...items].sort((a, b) => Math.abs(Number(b.tces) || 0) - Math.abs(Number(a.tces) || 0))[0];
  const name = top?.mesh_class_name?.trim() || 'Unknown';
  const score = formatTcesScore(top?.tces);
  const topLabel = score ? `${name} (${score})` : name;
  return `${topLabel} · ${getGtpClassCountLabel(items)}`;
}

export function getTcesBarPercent(tces: number, maxAbsTces: number): number {
  if (!Number.isFinite(tces) || maxAbsTces <= 0) return 0;
  return Math.max(0, Math.min(100, (Math.abs(tces) / maxAbsTces) * 100));
}

export function buildGtpChartRows(items: GeneGtpItem[]): GtpChartRow[] {
  const byName = new Map<string, GeneGtpItem>();
  const extras: GeneGtpItem[] = [];

  items.forEach(item => {
    const name = item.mesh_class_name?.trim();
    if (!name) return;
    if ((GTP_PLOT_EXCLUDED_CLASSES as readonly string[]).includes(name)) return;
    if ((GTP_CHART_MESH_CLASSES as readonly string[]).includes(name)) {
      byName.set(name, item);
    } else {
      extras.push(item);
    }
  });

  const toRow = (name: string, item?: GeneGtpItem): GtpChartRow => {
    const tces = tcesNumber(item?.tces);
    return {
      id: item?.mesh_class_id?.trim() || name,
      label: truncateLabel(name),
      fullName: name,
      tces,
      color: tces > 0 ? GTP_POSITIVE_COLOR : tces < 0 ? GTP_NEGATIVE_COLOR : GTP_NEUTRAL_COLOR,
    };
  };

  const meshIndex = new Map<string, number>(GTP_CHART_MESH_CLASSES.map((name, index) => [name, index]));

  return [
    ...GTP_CHART_MESH_CLASSES.map(name => toRow(name, byName.get(name))),
    ...extras.map(item => toRow(item.mesh_class_name?.trim() || 'Unknown', item)),
  ].sort((a, b) => {
    if (a.tces !== b.tces) return b.tces - a.tces;
    return (
      (meshIndex.get(a.fullName) ?? Number.MAX_SAFE_INTEGER) -
      (meshIndex.get(b.fullName) ?? Number.MAX_SAFE_INTEGER)
    );
  });
}

export function formatGtpList(value: unknown): string {
  if (value == null || value === '') return '-';
  if (typeof value === 'string') return value.trim() || '-';

  if (Array.isArray(value)) {
    if (value.length === 0) return '-';
    const labels = value.map(formatGtpItem).filter(Boolean);
    return labels.length > 0 ? labels.join(' | ') : '-';
  }

  return formatGtpItem(value) || '-';
}
